import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { Add } from "iconsax-react";
import { Input, Badge, Button } from "@/shared/ui";
import type { BadgeColor } from "@/shared/ui";
import type { Tag, TagColor } from "../../../types";
import { TAG_COLORS } from "../../../consts";

interface Props {
  label: string;
  value: Tag[];
  onChange: (tags: Tag[]) => void;
  addLabel: string;
  placeholder?: string;
}

const DOT_BG_MAP: Record<TagColor, string> = {
  gray: "bg-grey-500",
  info: "bg-blue-500",
  positive: "bg-green-500",
  notice: "bg-orange-500",
  negative: "bg-red-500",
  primary: "bg-brand-500",
  yellow: "bg-yellow-500",
  indigo: "bg-indigo-500",
  pink: "bg-pink-500",
  lime: "bg-lime-500",
  cyan: "bg-cyan-500",
};

export default function TagInput({ label, value, onChange, addLabel, placeholder }: Props) {
  const { t } = useTranslation("TasksPage");
  const [isAdding, setIsAdding] = useState(false);
  const [newText, setNewText] = useState("");
  const [newColor, setNewColor] = useState<TagColor>("gray");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(function handleClickOutside() {
    if (!isAdding) return;

    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsAdding(false);
        setNewText("");
        setNewColor("gray");
      }
    }

    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [isAdding]);

  function handleRemove(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  function handleConfirmAdd() {
    const trimmed = newText.trim();
    if (!trimmed) return;
    onChange([...value, { text: trimmed, color: newColor }]);
    setNewText("");
    setNewColor("gray");
    setIsAdding(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      handleConfirmAdd();
    }
    if (e.key === "Escape") {
      e.stopPropagation();
      setIsAdding(false);
      setNewText("");
      setNewColor("gray");
    }
  }

  return (
    <div ref={containerRef} className="flex flex-col gap-2">
      <span className="text-label-sm content-base-primary">{label}</span>

      <div className="flex flex-wrap items-center gap-2">
        {value.map((tag, index) => (
          <Badge
            key={index}
            variant="soft"
            color={tag.color as BadgeColor}
            text={tag.text}
            onRemove={() => handleRemove(index)}
          />
        ))}

        <div className="relative">
          <button
            type="button"
            onClick={() => setIsAdding(true)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-full border border-dashed surface-base-stroke text-body-regular-sm content-base-secondary hover:content-base-primary cursor-pointer transition-colors">
            <Add size={14} color="currentColor" />
            {addLabel}
          </button>

          {isAdding && (
            <div className="absolute left-0 bottom-full mb-2 z-[10200] flex flex-col gap-3 p-3 rounded-xl border surface-base-stroke surface-base-fill w-80">
              <Input
                placeholder={placeholder ?? ""}
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
              />
              <div className="flex items-center gap-1.5">
                {TAG_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    title={t(`tagColor.${color}`)}
                    onClick={() => setNewColor(color)}
                    className={cn(
                      "w-5 h-5 rounded-full cursor-pointer shrink-0 transition-opacity",
                      DOT_BG_MAP[color],
                      newColor === color ? "ring-2 ring-offset-1 ring-grey-400" : "opacity-60 hover:opacity-100",
                    )}
                  />
                ))}
              </div>
              <Button
                type="button"
                variant="primary"
                size="sm"
                className="self-start"
                disabled={!newText.trim()}
                onClick={handleConfirmAdd}>
                {t("taskForm.actions.confirmAddTag")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
